import { DescriptionOutlined, ExpandLess, ExpandMore, OpenInNew } from '@mui/icons-material'
import { Box, Button, Collapse, Divider, Grid, IconButton, Typography, useMediaQuery } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { CustomerDocumentDto } from '../../../interactors/gen/backendClient'
import { getCustomerDocumentsSrc } from '../../../utils/S3-links'
import { blueSky, theme } from '../../theme'
import { DeleteDocumentModal } from './DeleteDocumentModal'
import { useAccountsStore } from '../../../store/AccountsStore'
import { fullName } from '../../../domain/Account/AccountDisplay'
import { DisplayChipLikeTextField } from '../../common/DisplayChipLikeTextField'
import {
  getReadableCustomerDocumentType,
  getColorForCustomerDocumentType,
} from '../../../domain/Customer/CustomerDisplay'

interface DocumentItemProps {
  document: CustomerDocumentDto
  setDocumentsList: React.Dispatch<React.SetStateAction<CustomerDocumentDto[]>>
}

export const DocumentItem: React.FC<DocumentItemProps> = ({ document, setDocumentsList }) => {
  const accountsStore = useAccountsStore()
  const members = accountsStore.members
  const member = members.find((m) => m.id === document.uploadedBy)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:1099px)')
  const showUploaderInline = useMediaQuery('(min-width:1050px)')
  const [isExpanded, setIsExpanded] = useState(false)

  const documentUrl = useMemo(() => getCustomerDocumentsSrc(document.documentKey), [document.documentKey])
  const documentLabel =
    document.type === 'others'
      ? document.name || 'Autre document'
      : getReadableCustomerDocumentType(document.type) || ''
  const secondaryLabelValue = document.reference || '-'
  const uploadedAt = new Date(document.uploadedAt).toLocaleDateString()
  const showUploadedInline = !isMobile
  const showDesktopConsultButton = !isMobile && !isTablet

  return (
    <Box
      sx={{
        py: { xs: 1.5, sm: 1.75 },
        borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        columnSpacing={{ xs: 1, sm: 1.25, md: 1.5 }}
        sx={{ margin: 0, width: '100%' }}
      >
        <Grid item sx={{ width: { xs: 36, sm: 42 }, flex: '0 0 auto' }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: 2.5,
              backgroundColor: 'rgba(105, 141, 172, 0.10)',
              border: '1px solid rgba(105, 141, 172, 0.16)',
            }}
          >
            <DescriptionOutlined sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 }, color: blueSky }} />
          </Box>
        </Grid>

        <Grid item xs zeroMinWidth>
          {showUploadedInline ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: showUploaderInline
                  ? 'minmax(210px, 0.85fr) minmax(120px, 0.55fr) minmax(170px, 0.72fr) minmax(145px, 0.7fr)'
                  : 'minmax(210px, 0.95fr) minmax(125px, 0.58fr) minmax(190px, 0.78fr)',
                columnGap: { sm: 1.75, md: 2.5 },
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 42 }}>
                <DisplayChipLikeTextField
                  label=""
                  value={documentLabel}
                  fullWidth
                  documentPage
                  color={getColorForCustomerDocumentType(document.type) || blueSky}
                  compact
                  borderless
                  emphasis
                  maxChipWidth={250}
                />
              </Box>

              <Box
                minWidth={0}
                sx={{
                  minHeight: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A8197',
                    fontSize: { xs: 10.5, sm: 11 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  Mis en ligne le
                </Typography>
                <Typography
                  sx={{
                    color: '#203864',
                    fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                    fontWeight: 400,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={`Mis en ligne le : ${uploadedAt}`}
                >
                  {uploadedAt}
                </Typography>
              </Box>

              <Box
                minWidth={0}
                sx={{
                  minHeight: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A8197',
                    fontSize: { xs: 10.5, sm: 11 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  Libellé
                </Typography>
                <Typography
                  sx={{
                    color: '#203864',
                    fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                    fontWeight: 400,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={secondaryLabelValue}
                >
                  {secondaryLabelValue}
                </Typography>
              </Box>

              {showUploaderInline && member && (
                <Box
                  minWidth={0}
                  sx={{
                    minHeight: 42,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7A8197',
                      fontSize: { xs: 10.5, sm: 11 },
                      fontWeight: 500,
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Par
                  </Typography>
                  <Typography
                    sx={{
                      color: '#203864',
                      fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                      fontWeight: 400,
                      lineHeight: 1.25,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={fullName(member)}
                  >
                    {fullName(member)}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 36 }}>
              <DisplayChipLikeTextField
                label=""
                value={documentLabel}
                fullWidth
                documentPage
                color={getColorForCustomerDocumentType(document.type) || blueSky}
                compact
                borderless
                emphasis
                maxChipWidth={220}
              />
            </Box>
          )}
        </Grid>

        <Grid item sx={{ flex: '0 0 auto' }}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={{ xs: 0.15, sm: 0.35 }}>
            <IconButton
              size="small"
              onClick={() => setIsExpanded((prev) => !prev)}
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                borderRadius: 2,
                color: '#D39A12',
                backgroundColor: 'rgba(243, 172, 0, 0.08)',
                '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
              }}
            >
              {isExpanded ? (
                <ExpandLess sx={{ fontSize: { xs: 18, sm: 20 } }} />
              ) : (
                <ExpandMore sx={{ fontSize: { xs: 18, sm: 20 } }} />
              )}
            </IconButton>

            {showDesktopConsultButton ? (
              <Button
                component="a"
                href={documentUrl}
                target="_blank"
                rel="noreferrer"
                variant="outlined"
                sx={{
                  minHeight: 34,
                  px: 1.6,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12.5,
                  fontWeight: 700,
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  borderColor: 'rgba(122, 129, 151, 0.24)',
                  color: '#203864',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'white',
                    borderColor: 'rgba(122, 129, 151, 0.35)',
                  },
                }}
              >
                Consulter
              </Button>
            ) : (
              <IconButton
                size="small"
                component="a"
                href={documentUrl}
                target="_blank"
                rel="noreferrer"
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: 2,
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(32, 56, 100, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
                }}
              >
                <OpenInNew sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            )}

            <DeleteDocumentModal
              documentId={document.id}
              setDocumentsList={setDocumentsList}
              disabled={!accountsStore.isAdmin()}
              iconOnly
            />
          </Box>
        </Grid>
      </Grid>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.15,
            ml: { xs: 5, sm: 6 },
            pl: { xs: 0.25, sm: 0.5 },
          }}
        >
          <Divider sx={{ mb: 1.15, borderColor: 'rgba(122, 129, 151, 0.14)' }} />

          <Box
            display="grid"
            gridTemplateColumns={
              isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))'
            }
            gap={{ xs: 1, sm: 1.15, md: 1.35 }}
          >
            {!showUploadedInline && (
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.35 }}
                >
                  Mis en ligne le
                </Typography>
                <Typography sx={{ color: '#203864', fontSize: 13, fontWeight: 400, lineHeight: 1.3 }}>
                  {uploadedAt}
                </Typography>
              </Box>
            )}

            {member && (!showUploadedInline || !showUploaderInline) && (
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.35 }}
                >
                  Par
                </Typography>
                <Typography
                  sx={{
                    color: '#203864',
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={fullName(member)}
                >
                  {fullName(member)}
                </Typography>
              </Box>
            )}

            <Box minWidth={0}>
              <Typography
                variant="caption"
                sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.35 }}
              >
                Libellé
              </Typography>
              <Typography sx={{ color: '#203864', fontSize: 13, fontWeight: 400, lineHeight: 1.3 }}>
                {secondaryLabelValue}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}
